import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import '../../styles/global.css';

interface ProductionLine {
  id: number;
  name: string;
}

interface ImportedFile {
  id: number;
  original_filename: string;
  items_count: number;
  created_at: string;
}

interface Assignment {
  id: number;
  production_line_id: number;
  production_line: {
    id: number;
    name: string;
  } | null;
  week: string;
  status: string;
  user_id: number | null;
  user: {
    id: number;
    name: string;
  } | null;
  items: Array<{
    id: number;
    material: string;
    nazwa_oryg: string;
    importedFile?: {
      id: number;
      original_filename: string;
    };
  }>;
  imported_files?: Array<{
    id: number;
    original_filename: string;
  }>;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

const ManageAssignments: React.FC = () => {
  const { isAdmin } = useAuth();
  const { t } = useLanguage();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [lines, setLines] = useState<ProductionLine[]>([]);
  const [importedFiles, setImportedFiles] = useState<ImportedFile[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<Assignment | null>(null);
  const [formData, setFormData] = useState({
    production_line_id: '',
    week: '',
    imported_file_id: '',
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (isAdmin) {
      Promise.all([fetchAssignments(), fetchLines(), fetchImportedFiles()]).finally(() => setFetching(false));
    } else {
      setFetching(false);
    }
  }, [isAdmin]);

  const fetchAssignments = async () => {
    try {
      const response = await api.get('/production-assignments');
      setAssignments(response.data);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  const fetchLines = async () => {
    try {
      const response = await api.get('/production-lines');
      setLines(response.data);
    } catch (error) {
      console.error('Error fetching production lines:', error);
    }
  };

  const fetchImportedFiles = async () => {
    try {
      const response = await api.get('/imported-files');
      setImportedFiles(response.data);
    } catch (error) {
      console.error('Error fetching imported files:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.production_line_id || !formData.week || !formData.imported_file_id) {
      setMessage(t('assignments.fillAllFields'));
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      if (editingAssignment) {
        await api.put(`/production-assignments/${editingAssignment.id}`, formData);
        setMessage(t('assignments.assignmentUpdated'));
      } else {
        await api.post('/production-assignments', formData);
        setMessage(t('assignments.assignmentCreated'));
      }
      fetchAssignments();
      setShowForm(false);
      setEditingAssignment(null);
      setFormData({ production_line_id: '', week: '', imported_file_id: '' });
    } catch (error: any) {
      setMessage(error.response?.data?.message || t('common.errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (assignment: Assignment) => {
    setEditingAssignment(assignment);
    setFormData({
      production_line_id: assignment.production_line_id.toString(),
      week: assignment.week,
      imported_file_id: '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('assignments.confirmDelete'))) return;

    try {
      await api.delete(`/production-assignments/${id}`);
      setMessage(t('assignments.assignmentDeleted'));
      fetchAssignments();
    } catch (error: any) {
      setMessage(error.response?.data?.message || t('assignments.couldNotDelete'));
    }
  };

  const getStatusStyle = (status: string): React.CSSProperties => {
    switch (status) {
      case 'pending':
        return { backgroundColor: '#fef3c7', color: '#92400e' };
      case 'in_progress':
        return { backgroundColor: '#dbeafe', color: '#1e40af' };
      case 'completed':
        return { backgroundColor: '#d1fae5', color: '#065f46' };
      default:
        return { backgroundColor: '#e2e8f0', color: '#4a5568' };
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return t('assignments.statusPending');
      case 'in_progress':
        return t('assignments.statusInProgress');
      case 'completed':
        return t('assignments.statusCompleted');
      default:
        return status;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isAdmin) {
    return <div>{t('assignments.noPermission')}</div>;
  }

  const isError = message.includes('błąd') || message.toLowerCase().includes('nie udało') || message.includes('wypełnić');

  const handleOpenCreate = () => {
    setShowForm(true);
    setEditingAssignment(null);
    setFormData({ production_line_id: '', week: '', imported_file_id: '' });
    setMessage('');
  };

  return (
    <div>
      <div className="page-header" style={{ padding: '8px 0', marginBottom: 20 }}>
        <h1
          style={{
            color: '#2d3748',
            background: 'none',
            WebkitBackgroundClip: 'initial',
            WebkitTextFillColor: '#2d3748',
            backgroundClip: 'initial',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span style={{ WebkitTextFillColor: 'initial' }}>📋</span>
          {t('assignments.title')}
        </h1>
        <button
          onClick={handleOpenCreate}
          className="btn"
          style={{ backgroundColor: '#667eea', color: '#ffffff', fontWeight: 600 }}
        >
          + {t('assignments.addNew')}
        </button>
      </div>

      {message && (
        <div
          style={{
            marginBottom: 20,
            padding: '12px 16px',
            backgroundColor: isError ? '#fee2e2' : '#d1fae5',
            color: isError ? '#991b1b' : '#065f46',
            borderRadius: 8,
            fontWeight: 500,
          }}
        >
          {message}
        </div>
      )}

      {fetching ? (
        <div className="loading-spinner">
          <div className="spinner" />
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr style={{ backgroundColor: '#667eea', color: 'white' }}>
                  <th style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>ID</th>
                  <th style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('assignments.line')}</th>
                  <th style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('assignments.week')}</th>
                  <th style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('assignments.file')}</th>
                  <th style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('assignments.status')}</th>
                  <th style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('assignments.user')}</th>
                  <th style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('assignments.items')}</th>
                  <th style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('assignments.createdAt')}</th>
                  <th style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('assignments.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {assignments.length === 0 && (
                  <tr>
                    <td
                      colSpan={9}
                      style={{
                        padding: '48px 20px',
                        textAlign: 'center',
                        color: '#718096',
                        fontStyle: 'italic',
                      }}
                    >
                      {t('assignments.empty')}
                    </td>
                  </tr>
                )}
                {assignments.map((assignment) => {
                  let displayFiles: string[] = [];
                  if (assignment.imported_files && assignment.imported_files.length > 0) {
                    displayFiles = assignment.imported_files.map((f) => f.original_filename).filter(Boolean);
                  } else if (assignment.items && assignment.items.length > 0) {
                    const fileNames = assignment.items
                      .map((item) => item.importedFile?.original_filename)
                      .filter(Boolean)
                      .filter((value, index, self) => self.indexOf(value) === index);
                    displayFiles = fileNames as string[];
                  }

                  const editDisabled = assignment.status === 'completed' || assignment.status === 'in_progress';
                  const deleteDisabled = assignment.status === 'in_progress';

                  return (
                    <tr key={assignment.id} style={{ borderBottom: '1px solid #e1e8ed' }}>
                      <td style={{ padding: '14px 12px', color: '#4a5568' }}>{assignment.id}</td>
                      <td style={{ padding: '14px 12px', fontWeight: 600, color: '#2d3748' }}>
                        {assignment.production_line?.name || assignment.production_line_id || t('common.notAvailable')}
                      </td>
                      <td style={{ padding: '14px 12px', color: '#4a5568' }}>{assignment.week || '-'}</td>
                      <td style={{ padding: '14px 12px', fontSize: 13, maxWidth: 300, wordBreak: 'break-word' }}>
                        {displayFiles.length > 0 ? (
                          displayFiles.map((filename, idx) => (
                            <div key={idx} style={{ marginBottom: idx < displayFiles.length - 1 ? 4 : 0, color: '#2d3748' }}>
                              📄 {filename}
                            </div>
                          ))
                        ) : (
                          <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>{t('assignments.noFile')}</span>
                        )}
                      </td>
                      <td style={{ padding: '14px 12px', textAlign: 'center' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            borderRadius: 12,
                            fontSize: 12,
                            fontWeight: 600,
                            ...getStatusStyle(assignment.status),
                          }}
                        >
                          {getStatusLabel(assignment.status)}
                        </span>
                      </td>
                      <td style={{ padding: '14px 12px', color: '#4a5568' }}>
                        {assignment.user?.name || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>{t('assignments.unassigned')}</span>}
                      </td>
                      <td style={{ padding: '14px 12px', textAlign: 'center', color: '#4a5568' }}>{assignment.items?.length || 0}</td>
                      <td style={{ padding: '14px 12px', color: '#4a5568', fontSize: 13 }}>{formatDate(assignment.created_at)}</td>
                      <td style={{ padding: '14px 12px' }}>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => handleEdit(assignment)}
                            disabled={editDisabled}
                            className="btn"
                            style={{
                              padding: '6px 12px',
                              fontSize: 13,
                              backgroundColor: editDisabled ? '#e5e7eb' : 'white',
                              color: editDisabled ? '#9ca3af' : '#667eea',
                              border: `2px solid ${editDisabled ? '#e5e7eb' : '#667eea'}`,
                              cursor: editDisabled ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {t('common.edit')}
                          </button>
                          <button
                            onClick={() => handleDelete(assignment.id)}
                            disabled={deleteDisabled}
                            className="btn"
                            style={{
                              padding: '6px 12px',
                              fontSize: 13,
                              backgroundColor: deleteDisabled ? '#e5e7eb' : '#ef4444',
                              color: deleteDisabled ? '#9ca3af' : 'white',
                              cursor: deleteDisabled ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {t('common.delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: 12,
              maxWidth: 500,
              width: '90%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <h2 style={{ marginTop: 0, color: '#667eea' }}>
              {editingAssignment ? t('assignments.editAssignment') : t('assignments.addAssignment')}
            </h2>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#4a5568' }}>
                  {t('assignments.line')} *
                </label>
                <select
                  value={formData.production_line_id}
                  onChange={(e) => setFormData({ ...formData, production_line_id: e.target.value })}
                  required
                  className="input"
                >
                  <option value="">{t('assignments.selectLine')}</option>
                  {lines.map((line) => (
                    <option key={line.id} value={line.id}>
                      {line.name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#4a5568' }}>
                  {t('assignments.weekLabel')} *
                </label>
                <input
                  type="text"
                  value={formData.week}
                  onChange={(e) => setFormData({ ...formData, week: e.target.value })}
                  placeholder="2024-01"
                  required
                  className="input"
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#4a5568' }}>
                  {t('assignments.importedFile')} *
                </label>
                <select
                  value={formData.imported_file_id}
                  onChange={(e) => setFormData({ ...formData, imported_file_id: e.target.value })}
                  required
                  className="input"
                >
                  <option value="">{t('assignments.selectFile')}</option>
                  {importedFiles.map((file) => (
                    <option key={file.id} value={file.id}>
                      {file.original_filename} ({file.items_count})
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingAssignment(null);
                    setFormData({ production_line_id: '', week: '', imported_file_id: '' });
                  }}
                  className="btn"
                  style={{ backgroundColor: '#6c757d', color: 'white' }}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn"
                  style={{ backgroundColor: '#667eea', color: 'white', opacity: loading ? 0.6 : 1 }}
                >
                  {loading ? t('common.saving') : editingAssignment ? t('common.update') : t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageAssignments;
